import React, { useEffect, useId, useRef } from 'react';
import {
  FaBookOpen,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaMemory,
  FaMicrochip,
  FaNetworkWired,
  FaPlay,
  FaPowerOff,
  FaShieldAlt,
  FaTimes,
} from 'react-icons/fa';
import { Badge, Button } from './Primitives';

function HelpSection({ icon: Icon, title, children, tone = 'text-primary' }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white shadow-sm dark:bg-gray-800 ${tone}`}>
          <Icon size={15} aria-hidden="true" />
        </span>
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="space-y-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {children}
      </div>
    </section>
  );
}

function StateLegend() {
  return (
    <div className="flex flex-wrap gap-2">
      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
        <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
        Accesa
      </Badge>
      <Badge className="bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
        <span className="h-2 w-2 rounded-full bg-gray-400" aria-hidden="true" />
        Spenta
      </Badge>
      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
        <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
        In transizione / attenzione
      </Badge>
    </div>
  );
}

/**
 * Guida contestuale della dashboard. È intenzionalmente nel frontend: spiega
 * il significato operativo dei dati senza esporre comandi, credenziali o
 * dettagli della connessione SSH.
 */
export default function EsxiHelpModal({ onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const focusTimer = setTimeout(() => closeRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const controls = Array.from(
        dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) || [],
      ).filter((element) => !element.disabled);
      if (controls.length === 0) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown, true);
      previousFocusRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-gray-950/55 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <header className="relative overflow-hidden border-b border-gray-100 bg-gradient-to-r from-primary/10 via-white to-sky-50 p-5 dark:border-gray-700 dark:from-primary/20 dark:via-gray-800 dark:to-sky-950/30 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-md">
                <FaBookOpen size={19} aria-hidden="true" />
              </span>
              <div>
                <h2 id={titleId} className="text-xl font-bold text-gray-900 dark:text-white">
                  Guida agli ambienti di test
                </h2>
                <p id={descriptionId} className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  Come leggere i dati e usare in sicurezza le azioni sulle VM.
                </p>
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-white hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 dark:hover:text-white"
              aria-label="Chiudi la guida"
            >
              <FaTimes size={17} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="overflow-y-auto p-4 sm:p-6">
          <div className="mb-5 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200">
            <div className="flex items-start gap-2">
              <FaInfoCircle className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />
              <p>
                La dashboard mostra l’inventario dell’host ESXi 1. I dati
                operativi arrivano da ESXi; ambiente, prodotto, ruolo e IP
                attesi arrivano dal catalogo SiSketron.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <HelpSection icon={FaCheckCircle} title="Stati e aggiornamento" tone="text-emerald-500">
              <StateLegend />
              <p>
                La data in alto indica quando ESXi è stato letto. Il badge
                “Dati non aggiornati” segnala che è stata usata una copia
                precedente perché l’host non era raggiungibile.
              </p>
              <p>
                Dopo un’azione riuscita lo stato cambia subito; in parallelo
                parte una lettura live per aggiornare Tools, IP e consumi.
              </p>
            </HelpSection>

            <HelpSection icon={FaNetworkWired} title="Indirizzi IP" tone="text-sky-500">
              <p>
                <strong>Rilevato</strong> significa che l’indirizzo è stato
                comunicato in tempo reale da VMware Tools.
              </p>
              <p>
                <strong>Atteso</strong> significa che proviene dal catalogo:
                resta visibile anche a VM spenta, ma non prova che in quel
                momento la VM stia realmente usando quell’indirizzo.
              </p>
              <p>
                Se IP rilevato e catalogato non coincidono, la scheda lo
                evidenzia per richiedere una verifica.
              </p>
            </HelpSection>

            <HelpSection icon={FaMicrochip} title="CPU e memoria" tone="text-violet-500">
              <div className="flex items-center gap-2">
                <FaMicrochip className="text-sky-500" aria-hidden="true" />
                <p>Il riepilogo host confronta CPU usata e capacità complessiva.</p>
              </div>
              <div className="flex items-center gap-2">
                <FaMemory className="text-primary" aria-hidden="true" />
                <p>La memoria host mostra uso corrente e memoria fisica totale.</p>
              </div>
              <p>
                Per ogni VM la RAM configurata è sempre nota. CPU, memoria
                runtime e uptime sono significativi soltanto quando la VM è
                accesa; a VM spenta vengono mostrati come non disponibili.
              </p>
            </HelpSection>

            <HelpSection icon={FaShieldAlt} title="Azioni sicure" tone="text-amber-500">
              <div className="flex items-start gap-2">
                <FaPlay className="mt-1 shrink-0 text-sky-500" aria-hidden="true" />
                <p><strong>Accendi</strong> avvia la VM e verifica che raggiunga lo stato acceso.</p>
              </div>
              <div className="flex items-start gap-2">
                <FaPowerOff className="mt-1 shrink-0 text-amber-500" aria-hidden="true" />
                <p>
                  <strong>Spegni correttamente</strong> richiede uno shutdown
                  guest tramite VMware Tools. Non viene mai eseguito un
                  power-off forzato.
                </p>
              </div>
              <p>
                Subito dopo un’accensione, lo shutdown può restare
                temporaneamente disabilitato finché VMware Tools non risulta
                pronto.
              </p>
            </HelpSection>
          </div>

          <section className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/25">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
              <div className="text-sm text-amber-900 dark:text-amber-200">
                <h3 className="font-semibold">Prima di eseguire un’azione</h3>
                <p className="mt-1">
                  Verifica nome, ambiente, prodotto e ruolo della VM. Le
                  operazioni sono accodate e rivalidate dal backend; chiudere
                  la pagina non annulla un comando già inviato.
                </p>
              </div>
            </div>
          </section>
        </div>

        <footer className="flex justify-end border-t border-gray-100 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40">
          <Button variant="primary" onClick={onClose}>
            Ho capito
          </Button>
        </footer>
      </div>
    </div>
  );
}
